import React, { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { FeatureProvider } from './contexts/FeatureContext';
import { SocketProvider } from './contexts/SocketContext';
import { ToastProvider } from './contexts/ToastContext';
import { CallProvider } from './contexts/CallContext';
import CallOverlay from './components/CallOverlay';
import { GroupCallProvider } from './contexts/GroupCallContext';
import GroupCallOverlay from './components/GroupCallOverlay';
import PrivateRoute from './components/PrivateRoute';
import AppLayout from './components/AppLayout';
import ShareIntentHandler from './components/ShareIntentHandler';
import NativeMobileBridge from './components/NativeMobileBridge';
import Login from './pages/Login';
import Register from './pages/Register';
import Home from './pages/Home';
import Explore from './pages/Explore';
import './App.css';

// Home/Explore (landing pages) and auth stay in the startup bundle; everything
// else is downloaded when first visited, which keeps first launch responsive.
const Profile = lazy(() => import('./pages/Profile'));
const Messages = lazy(() => import('./pages/Messages'));
const Notifications = lazy(() => import('./pages/Notifications'));
const MobilePostComments = lazy(() => import('./pages/MobilePostComments'));
const MobileCommentDetail = lazy(() => import('./pages/MobileCommentDetail'));
const PostDetail = lazy(() => import('./pages/PostDetail'));
const Search = lazy(() => import('./pages/Search'));
const Hashtag = lazy(() => import('./pages/Hashtag'));
const FollowList = lazy(() => import('./pages/FollowList'));
const ComposePost = lazy(() => import('./pages/ComposePost'));
const ShareTarget = lazy(() => import('./pages/ShareTarget'));
const Bookmarks = lazy(() => import('./pages/Bookmarks'));
const Settings = lazy(() => import('./pages/Settings'));
const Communities = lazy(() => import('./pages/Communities'));
const CreateCommunity = lazy(() => import('./pages/CreateCommunity'));
const CommunityDetail = lazy(() => import('./pages/CommunityDetail'));
const EditCommunity = lazy(() => import('./pages/EditCommunity'));
const HalqaPage = lazy(() => import('./pages/HalqaPage'));
const Lamha = lazy(() => import('./pages/Lamha'));
const LamhaStudio = lazy(() => import('./pages/LamhaStudio'));
const Admin = lazy(() => import('./pages/Admin'));
const InfoPage = lazy(() => import('./pages/InfoPage'));
const Support = lazy(() => import('./pages/Support'));
const Contact = lazy(() => import('./pages/Contact'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const Marketplace = lazy(() => import('./pages/Marketplace'));
const MarketplaceItem = lazy(() => import('./pages/MarketplaceItem'));
const CreateListing = lazy(() => import('./pages/CreateListing'));
const ImpactHub = lazy(() => import('./pages/ImpactHub'));

// Warm up the most-used screens once the app is idle, so the first tap on them is instant
const prefetchCommonPages = () => {
  const run = () => {
    import('./pages/Messages');
    import('./pages/Notifications');
    import('./pages/Profile');
    import('./pages/PostDetail');
  };
  if ('requestIdleCallback' in window) window.requestIdleCallback(run, { timeout: 4000 });
  else setTimeout(run, 2500);
};

function PageViewTracker() {
  const location = useLocation();

  React.useEffect(() => {
    if (typeof window.gtag === 'function') {
      window.gtag('config', 'G-LPC6F6RXSC', {
        page_path: location.pathname + location.search,
      });
    }
  }, [location]);

  return null;
}

function IndexRedirect() {
  const { user } = useAuth();
  return <Navigate to={user ? '/home' : '/explore'} replace />;
}

function App() {
  useEffect(prefetchCommonPages, []);

  return (
    <AuthProvider>
      <FeatureProvider>
        <SocketProvider>
        <ToastProvider>
          <CallProvider>
            <GroupCallProvider>
              <Router>
                <PageViewTracker />
                <NativeMobileBridge />
                <div className="min-h-screen bg-white dark:bg-black text-gray-900 dark:text-gray-100">
                  <ShareIntentHandler />
                  <Suspense fallback={<div className="min-h-screen bg-white dark:bg-black" />}>
                  <Routes>
                    <Route path="/login" element={<Login />} />
                    <Route path="/register" element={<Register />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="/" element={<AppLayout />}>
                      <Route index element={<IndexRedirect />} />
                      <Route path="explore" element={<Explore />} />
                      <Route path="home" element={<PrivateRoute><Home /></PrivateRoute>} />
                      <Route path="profile/:handle" element={<Profile />} />
                      <Route path="u/:handle" element={<Profile />} />
                      <Route path="profile/:handle/:listType" element={<FollowList />} />
                      <Route path="search" element={<Search />} />
                      <Route path="hashtag/:tag" element={<Hashtag />} />
                      <Route path="tag/:tag" element={<Hashtag />} />
                      <Route path="notifications" element={<PrivateRoute><Notifications /></PrivateRoute>} />
                      <Route path="messages" element={<PrivateRoute><Messages /></PrivateRoute>} />
                      <Route path="messages/group/:groupId" element={<PrivateRoute><Messages /></PrivateRoute>} />
                      <Route path="messages/:targetUserId" element={<PrivateRoute><Messages /></PrivateRoute>} />
                      <Route path="follow/:handle/:listType" element={<FollowList />} />
                      <Route path="follow/:listType" element={<FollowList />} />
                      <Route path="bookmarks" element={<PrivateRoute><Bookmarks /></PrivateRoute>} />
                      <Route path="communities" element={<Communities />} />
                      <Route path="communities/create" element={<PrivateRoute><CreateCommunity /></PrivateRoute>} />
                      <Route path="communities/:id" element={<CommunityDetail />} />
                      <Route path="community/:id" element={<CommunityDetail />} />
                      <Route path="c/:id" element={<CommunityDetail />} />
                      <Route path="communities/:id/edit" element={<PrivateRoute><EditCommunity /></PrivateRoute>} />
                      <Route path="marketplace" element={<Marketplace />} />
                      <Route path="marketplace/item/:id" element={<MarketplaceItem />} />
                      <Route path="marketplace/create" element={<PrivateRoute><CreateListing /></PrivateRoute>} />
                      <Route path="marketplace/edit/:id" element={<PrivateRoute><CreateListing isEdit={true} /></PrivateRoute>} />
                      <Route path="impact" element={<ImpactHub />} />
                      <Route path="mouaawanah" element={<ImpactHub />} />
                      <Route path="hirfa" element={<ImpactHub />} />
                      <Route path="tahaqqoq" element={<ImpactHub />} />
                      <Route path="dirham" element={<ImpactHub />} />
                      <Route path="settings" element={<PrivateRoute><Settings /></PrivateRoute>} />
                      <Route path="settings/:section" element={<PrivateRoute><Settings /></PrivateRoute>} />
                      <Route path="about" element={<InfoPage doc="about" />} />
                      <Route path="terms" element={<InfoPage doc="terms" />} />
                      <Route path="privacy" element={<InfoPage doc="privacy" />} />
                      <Route path="guidelines" element={<InfoPage doc="guidelines" />} />
                      <Route path="support" element={<Support />} />
                      <Route path="contact" element={<Contact />} />
                      <Route path="admin" element={<PrivateRoute><Admin /></PrivateRoute>} />
                      <Route path="compose" element={<PrivateRoute><ComposePost /></PrivateRoute>} />
                      <Route path="share" element={<PrivateRoute><ShareTarget /></PrivateRoute>} />
                      <Route path="lamha" element={<Lamha />} />
                      <Route path="lamha/create" element={<PrivateRoute><LamhaStudio /></PrivateRoute>} />
                      <Route path="lamha/:postId" element={<Lamha />} />
                      <Route path="halqa" element={<PrivateRoute><HalqaPage /></PrivateRoute>} />
                      <Route path="halqa/:roomId" element={<PrivateRoute><HalqaPage /></PrivateRoute>} />
                      <Route path="mobile/post/:postId/comments" element={<PrivateRoute><MobilePostComments /></PrivateRoute>} />
                      <Route path="mobile/post/:postId/comment/:commentId" element={<PrivateRoute><MobileCommentDetail /></PrivateRoute>} />
                      <Route path="post/:postId" element={<PostDetail />} />
                      <Route path="post/:postId/comment/:commentId" element={<PostDetail />} />
                      <Route path="p/:postId" element={<PostDetail />} />
                      <Route path="p/:postId/comment/:commentId" element={<PostDetail />} />
                      <Route path="*" element={<IndexRedirect />} />
                    </Route>
                  </Routes>
                  </Suspense>
                </div>
              </Router>
              <CallOverlay />
              <GroupCallOverlay />
            </GroupCallProvider>
          </CallProvider>
        </ToastProvider>
      </SocketProvider>
      </FeatureProvider>
    </AuthProvider>
  );
}

export default App;
